import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useUserState } from '@/components/context/UserContext';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/shared/heading';
import { Download } from 'lucide-react';
import { downloadAmisPdf } from '@/v2/utils/downloadAmisPdf';
import RecommendationRegistrationButton from './RecommendationRegistrationButton';
import { recommendationRouteId } from './recommendationRouteId';

const dateText = (value) => {
  if (!value) return '–';
  const text = String(value).replace('T', ' ').slice(0, 16);
  return text.endsWith(' 00:00') ? text.slice(0, 10) : text;
};
const doctorText = (doctor) =>
  [doctor?.qualification, doctor?.name, doctor?.surname].filter(Boolean).join(' ') || '–';

const Field = ({ label, value }) => value != null && value !== '' ? (
  <div className="whitespace-pre-line"><span className="font-medium">{label}: </span>{value}</div>
) : null;

export default function DoctorRecommendationDetailsPage() {
  const { e025Id, serviceId } = useParams();
  const location = useLocation();
  const userState = useUserState();
  const { get, response } = useFetch('/UserAccount/DoctorRecommendations');
  const { get: getFile, response: fileResponse } = useFetch('/UserAccount/DownloadAmisFile');
  const [item, setItem] = useState(location.state?.recommendation ?? null);
  const [relatedRecommendations, setRelatedRecommendations] = useState(location.state?.relatedRecommendations ?? []);
  const [loading, setLoading] = useState(!location.state?.recommendation);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (location.state?.recommendation &&
        String(location.state.recommendation.e025Id) === e025Id &&
        recommendationRouteId(location.state.recommendation) === serviceId) {
      setItem(location.state.recommendation);
      setRelatedRecommendations(location.state.relatedRecommendations ?? [location.state.recommendation]);
      setLoading(false);
      return;
    }
    let active = true;
    setItem(null);
    setRelatedRecommendations([]);
    setLoading(true);
    get(`?espbiEsi=${encodeURIComponent(userState.espbiEsi ?? '')}`)
      .then((result) => {
        if (!active) return;
        setItem(response.ok && Array.isArray(result)
          ? result.find((row) => String(row.e025Id) === e025Id && recommendationRouteId(row) === serviceId)
          : null);
        setRelatedRecommendations(response.ok && Array.isArray(result)
          ? result.filter((row) => String(row.e025Id) === e025Id)
          : []);
        setError(!response.ok);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [e025Id, serviceId, userState.espbiEsi]);

  const record = item?.record;
  const recommendation = item?.recommendation;

  const downloadFile = async () => {
    if (!record?.file?.guid || !record?.file?.type) return;
    await downloadAmisPdf({
      getFile,
      response: fileResponse,
      query: `?fileType=${encodeURIComponent(record.file.type)}&fileGuid=${encodeURIComponent(record.file.guid)}&category=e025&espbiEsi=${encodeURIComponent(userState.espbiEsi ?? '')}`,
      fileName: record.file.fileName || `E025-${e025Id}`,
    });
  };

  return (
    <div className="space-y-4">
      <div className="px-4"><Link to="/userAccount/doctor-recommendations">Gydytojų rekomendacijos</Link> / {item?.serviceTitle || 'Informacija'}</div>
      {loading && <p className="px-4">Įkeliama...</p>}
      {!loading && !item && <p className="px-4" role="alert">{error ? 'Nepavyko gauti rekomendacijos.' : 'Rekomendacija nerasta.'}</p>}
      {item && (
        <>
          <Card>
            <CardHeader><Heading variant="userAccountDetailsTitle">Rekomendacija</Heading></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Paslauga" value={item.serviceTitle} />
              <Field label="Po kiek dienų atlikti" value={recommendation?.afterDays} />
              <Field label="Rekomenduojama data" value={dateText(recommendation?.plannedDate)} />
              <Field label="Rekomendavęs gydytojas" value={doctorText(recommendation?.practitioner)} />
              <div className="py-2">
                <RecommendationRegistrationButton recommendation={item} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><Heading variant="userAccountDetailsTitle">Ambulatorinio apsilankymo aprašymas</Heading></CardHeader>
            <CardContent className="space-y-3">
              <Field label="Įrašo ID" value={record?.id || e025Id} />
              <Field label="Data" value={dateText(record?.date)} />
              <Field label="Laikas" value={record?.time} />
              <Field label="Gydytojas" value={doctorText(record?.practitioner)} />
              <Field label="Nusiskundimai ir anamnezė" value={record?.complaints} />
              <Field label="Objektyvi būklė" value={record?.objectiveCondition} />
              <Field label="Atlikti tyrimai ir konsultacijos" value={record?.finishedDiagnosticReports} />
              <Field label="Tyrimų ir konsultacijų planas" value={record?.plannedDiagnosticReports} />
              <Field label="Rekomendacijos" value={record?.recommendations} />
              <Field label="Taikytas medikamentinis gydymas" value={record?.medicationStatement} />
              <Field label="Taikytas gydymas" value={record?.carePlan} />
              <Field label="Dokumento pavadinimas" value={record?.file?.fileName} />
              {record?.file?.guid && (
                <div className="py-2">
                  <Button variant="light" onClick={downloadFile}><Download className="mr-2 h-4 w-4" />Atsisiųsti PDF</Button>
                </div>
              )}
            </CardContent>
          </Card>
          {relatedRecommendations.length > 1 && (
            <Card>
              <CardHeader><Heading variant="userAccountDetailsTitle">Visos šio įrašo rekomendacijos</Heading></CardHeader>
              <CardContent className="space-y-4">
                {relatedRecommendations.map(({ serviceTitle, recommendation: service }, index) => (
                  <div key={`${service.serviceId}-${index}`} className="border-b pb-3 last:border-b-0">
                    <Field label="Paslauga" value={serviceTitle} />
                    <Field label="Po kiek dienų atlikti" value={service.afterDays} />
                    <Field label="Rekomenduojama data" value={dateText(service.plannedDate)} />
                    <Field label="Rekomendavęs gydytojas" value={doctorText(service.practitioner)} />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
